import React, { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Navigate } from 'react-router-dom'

const DEFAULT_PAYLOAD = JSON.stringify({ txnid: 'temp0001', amt: 20, item: '101' }, null, 2)

const MqttCommands = () => {
  const { userClaims } = useAuth()
  const [emqxIp, setEmqxIp] = useState('localhost')
  const [topic, setTopic] = useState('')
  const [payloadFormat, setPayloadFormat] = useState('json')
  const [payloadText, setPayloadText] = useState(DEFAULT_PAYLOAD)
  const [payloadError, setPayloadError] = useState('')
  const [sending, setSending] = useState(false)
  const [response, setResponse] = useState(null)
  const [requestError, setRequestError] = useState('')
  const [bannedClients, setBannedClients] = useState([])
  const [bansLoading, setBansLoading] = useState(false)
  const [bansError, setBansError] = useState('')
  const [banType, setBanType] = useState('clientid')
  const [banValue, setBanValue] = useState('')
  const [banReason, setBanReason] = useState('')
  const [selectedBans, setSelectedBans] = useState([])
  const [banOperation, setBanOperation] = useState('')
  const [banActionMessage, setBanActionMessage] = useState('')
  const [banActionError, setBanActionError] = useState('')

  const validatePayload = (text, format = payloadFormat) => {
    if (format === 'text') {
      return text.trim() ? '' : 'Plain text payload cannot be empty.'
    }

    try {
      JSON.parse(text)
      return ''
    } catch (e) {
      return `Invalid JSON: ${e.message}`
    }
  }

  const handlePayloadChange = (e) => {
    const text = e.target.value
    setPayloadText(text)
    setPayloadError(validatePayload(text, payloadFormat))
  }

  const handlePayloadFormatChange = (e) => {
    const format = e.target.value
    setPayloadFormat(format)
    setPayloadError(validatePayload(payloadText, format))
  }

  const handleSend = async () => {
    setRequestError('')
    setResponse(null)

    // Validate fields
    if (!topic.trim()) {
      setRequestError('Topic is required.')
      return
    }
    const payloadValidationError = validatePayload(payloadText, payloadFormat)
    if (payloadValidationError) {
      setPayloadError(payloadValidationError)
      return
    }

    const payload = payloadFormat === 'json' ? JSON.parse(payloadText) : payloadText
    setSending(true)
    try {
      const res = await fetch('/api/publish', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emqx_ip: emqxIp.trim(),
          topic: topic.trim(),
          payload,
        }),
      })
      const data = await res.json()
      setResponse({ ok: res.ok, status: res.status, data })
    } catch (err) {
      setRequestError(err.message || 'Request failed. Check the console for details.')
    } finally {
      setSending(false)
    }
  }

  const handleClear = () => {
    setTopic('')
    setPayloadFormat('json')
    setPayloadText(DEFAULT_PAYLOAD)
    setPayloadError('')
    setResponse(null)
    setRequestError('')
  }

  const normalizeBannedClients = (data) => {
    const list = Array.isArray(data)
      ? data
      : (data?.banned || data?.data || data?.items || [])

    return (Array.isArray(list) ? list : []).map((ban, index) => {
      const as = ban.as || ban.type || ban.banned_type || ''
      const who = ban.who || ban.value || ban.clientid || ban.username || ban.peerhost || ''
      return {
        ...ban,
        as,
        who,
        key: `${as}:${who}:${ban.at || ban.until || index}`,
      }
    }).filter(ban => ban.as && ban.who)
  }

  const getApiError = async (res) => {
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.error || data.message || `Request failed (HTTP ${res.status})`)
    }
    return data
  }

  const fetchBannedClients = async () => {
    setBansLoading(true)
    setBansError('')
    try {
      const broker = emqxIp.trim() || 'localhost'
      const res = await fetch(`/api/banned?emqx_ip=${encodeURIComponent(broker)}`, {
        credentials: 'include',
      })
      const data = await getApiError(res)
      setBannedClients(normalizeBannedClients(data))
      setSelectedBans([])
    } catch (err) {
      setBansError(err.message || 'Failed to load banned clients.')
    } finally {
      setBansLoading(false)
    }
  }

  useEffect(() => {
    if (userClaims?.sadmin) {
      fetchBannedClients()
    }
  }, [userClaims?.sadmin])

  const handleAddBan = async (event) => {
    event.preventDefault()
    setBanActionMessage('')
    setBanActionError('')

    if (!banValue.trim()) {
      setBanActionError('Enter a client ID, username, or source IP to ban.')
      return
    }

    setBanOperation('add')
    try {
      const res = await fetch('/api/banned', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          emqx_ip: emqxIp.trim() || 'localhost',
          as: banType,
          who: banValue.trim(),
          ...(banReason.trim() ? { reason: banReason.trim() } : {}),
        }),
      })
      await getApiError(res)
      setBanValue('')
      setBanReason('')
      setBanActionMessage('Banned client added successfully.')
      await fetchBannedClients()
    } catch (err) {
      setBanActionError(err.message || 'Failed to add banned client.')
    } finally {
      setBanOperation('')
    }
  }

  const removeBannedClient = async (ban) => {
    const res = await fetch('/api/banned/client', {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        emqx_ip: emqxIp.trim() || 'localhost',
        as: ban.as,
        who: ban.who,
      }),
    })
    await getApiError(res)
  }

  const handleRemoveBan = async (ban) => {
    setBanActionMessage('')
    setBanActionError('')
    setBanOperation(`remove:${ban.key}`)
    try {
      await removeBannedClient(ban)
      setBanActionMessage(`Removed ${ban.who} from the banned-client list.`)
      await fetchBannedClients()
    } catch (err) {
      setBanActionError(err.message || 'Failed to remove banned client.')
    } finally {
      setBanOperation('')
    }
  }

  const handleRemoveSelected = async () => {
    const selected = bannedClients.filter(ban => selectedBans.includes(ban.key))
    if (selected.length === 0) return

    setBanActionMessage('')
    setBanActionError('')
    setBanOperation('remove-selected')
    try {
      const results = await Promise.allSettled(selected.map(removeBannedClient))
      const failures = results.filter(result => result.status === 'rejected')
      if (failures.length > 0) {
        setBanActionError(`${failures.length} selected ban${failures.length === 1 ? '' : 's'} could not be removed.`)
      } else {
        setBanActionMessage(`Removed ${selected.length} banned client${selected.length === 1 ? '' : 's'}.`)
      }
      await fetchBannedClients()
    } catch (err) {
      setBanActionError(err.message || 'Failed to remove selected bans.')
    } finally {
      setBanOperation('')
    }
  }

  const handleClearBans = async () => {
    if (!window.confirm('Clear All will remove every banned client from this EMQX broker. Continue?')) return

    setBanActionMessage('')
    setBanActionError('')
    setBanOperation('clear')
    try {
      const broker = emqxIp.trim() || 'localhost'
      const res = await fetch(`/api/banned?emqx_ip=${encodeURIComponent(broker)}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      await getApiError(res)
      setBanActionMessage('All banned clients were removed.')
      await fetchBannedClients()
    } catch (err) {
      setBanActionError(err.message || 'Failed to clear banned clients.')
    } finally {
      setBanOperation('')
    }
  }

  const toggleBanSelection = (key) => {
    setSelectedBans(prev => prev.includes(key)
      ? prev.filter(selectedKey => selectedKey !== key)
      : [...prev, key])
  }

  const formatBanDate = (value) => {
    if (!value) return '-'
    try {
      const date = value?.toDate?.() || new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleString()
    } catch {
      return '-'
    }
  }

  // Redirect if not super admin
  if (!userClaims?.sadmin) {
    return <Navigate to="/devices" replace />
  }

  const isSuccess = response?.data?.status === 'success'
  const hasSubscriber = isSuccess && !response?.data?.emqx_response?.reason_code

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">MQTT Commands</h2>
              <p className="text-muted mb-0">
                <i className="fas fa-shield-alt text-warning me-2"></i>
                Super Admin Only — Publish payloads to EMQX topics
              </p>
            </div>
            <button
              className="btn btn-outline-secondary"
              onClick={handleClear}
              disabled={sending}
            >
              <i className="fas fa-eraser me-2"></i>
              Clear
            </button>
          </div>

          <div className="row">
            {/* Command Form */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white">
                  <h5 className="mb-0">
                    <i className="fas fa-paper-plane me-2"></i>
                    Send Command
                  </h5>
                </div>
                <div className="card-body">
                  {/* EMQX IP */}
                  <div className="mb-3">
                    <label className="form-label fw-medium">EMQX IP</label>
                    <input
                      type="text"
                      className="form-control"
                      value={emqxIp}
                      onChange={(e) => setEmqxIp(e.target.value)}
                      placeholder="localhost"
                      style={{ fontFamily: 'monospace' }}
                    />
                    <div className="form-text">IP or hostname of the EMQX broker (private network).</div>
                  </div>

                  {/* Topic */}
                  <div className="mb-3">
                    <label className="form-label fw-medium">Topic</label>
                    <input
                      type="text"
                      className={`form-control ${requestError && !topic.trim() ? 'is-invalid' : ''}`}
                      value={topic}
                      onChange={(e) => { setTopic(e.target.value); setRequestError('') }}
                      placeholder="/dev/vv00003/commands"
                      style={{ fontFamily: 'monospace' }}
                    />
                    {requestError && !topic.trim() && (
                      <div className="invalid-feedback">{requestError}</div>
                    )}
                  </div>

                  {/* Payload */}
                  <div className="mb-4">
                    <label className="form-label fw-medium">Payload Format</label>
                    <select
                      className="form-select mb-2"
                      value={payloadFormat}
                      onChange={handlePayloadFormatChange}
                      disabled={sending}
                    >
                      <option value="json">JSON</option>
                      <option value="text">Plain Text</option>
                    </select>
                    <label className="form-label fw-medium">
                      Payload <span className="text-muted fw-normal">({payloadFormat === 'json' ? 'JSON' : 'Plain Text'})</span>
                    </label>
                    <textarea
                      className={`form-control ${payloadError ? 'is-invalid' : ''}`}
                      rows={7}
                      value={payloadText}
                      onChange={handlePayloadChange}
                      placeholder={payloadFormat === 'json' ? DEFAULT_PAYLOAD : 'Enter plain text payload'}
                      style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    />
                    {payloadError && (
                      <div className="invalid-feedback">{payloadError}</div>
                    )}
                  </div>

                  {requestError && topic.trim() && (
                    <div className="alert alert-danger py-2">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {requestError}
                    </div>
                  )}

                  <button
                    className="btn btn-primary w-100"
                    onClick={handleSend}
                    disabled={sending || !!payloadError}
                  >
                    {sending ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Sending…
                      </>
                    ) : (
                      <>
                        <i className="fas fa-broadcast-tower me-2"></i>
                        Publish
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Response Panel */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-white d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">
                    <i className="fas fa-terminal me-2"></i>
                    Response
                  </h5>
                  {response && (
                    <span className={`badge ${isSuccess ? (hasSubscriber ? 'bg-success' : 'bg-warning text-dark') : 'bg-danger'}`}>
                      {isSuccess ? (hasSubscriber ? 'Delivered' : 'No Subscribers') : `HTTP ${response.status}`}
                    </span>
                  )}
                </div>
                <div className="card-body">
                  {!response ? (
                    <div className="text-center py-5 text-muted">
                      <i className="fas fa-broadcast-tower fa-3x mb-3 d-block opacity-25"></i>
                      <p className="mb-0">Response will appear here after sending</p>
                    </div>
                  ) : (
                    <>
                      {isSuccess && hasSubscriber && (
                        <div className="alert alert-success py-2 mb-3">
                          <i className="fas fa-check-circle me-2"></i>
                          Message delivered — subscriber received the payload.
                        </div>
                      )}
                      {isSuccess && !hasSubscriber && (
                        <div className="alert alert-warning py-2 mb-3">
                          <i className="fas fa-exclamation-circle me-2"></i>
                          Published but no matching subscribers on this topic.
                        </div>
                      )}
                      {!isSuccess && (
                        <div className="alert alert-danger py-2 mb-3">
                          <i className="fas fa-times-circle me-2"></i>
                          Request failed (HTTP {response.status}).
                        </div>
                      )}
                      <pre
                        className="bg-light rounded p-3 mb-0"
                        style={{ fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '320px', overflowY: 'auto' }}
                      >
                        {JSON.stringify(response.data, null, 2)}
                      </pre>
                    </>
                  )}
                </div>
              </div>

              {/* Reference */}
              <div className="card border-0 shadow-sm mt-3">
                <div className="card-header bg-white">
                  <h6 className="mb-0 text-muted">
                    <i className="fas fa-info-circle me-2"></i>
                    Topic format reference
                  </h6>
                </div>
                <div className="card-body py-2">
                  <table className="table table-sm table-borderless mb-0 small">
                    <tbody>
                      <tr>
                        <td className="text-muted pe-3">Commands</td>
                        <td><code>/dev/{'<deviceId>'}/commands</code></td>
                      </tr>
                      <tr>
                        <td className="text-muted pe-3">Device root</td>
                        <td><code>/dev/{'<deviceId>'}</code></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="card border-0 shadow-sm mt-4">
            <div className="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-0">
                  <i className="fas fa-user-slash me-2 text-danger"></i>
                  Banned Clients
                </h5>
                <small className="text-muted">Manage client IDs, usernames, and source IPs blocked by EMQX.</small>
              </div>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={fetchBannedClients}
                  disabled={bansLoading || !!banOperation}
                >
                  <i className={`fas fa-sync-alt me-1 ${bansLoading ? 'fa-spin' : ''}`}></i>
                  Refresh
                </button>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleClearBans}
                  disabled={bansLoading || !!banOperation || bannedClients.length === 0}
                >
                  {banOperation === 'clear' ? (
                    <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Clearing…</>
                  ) : (
                    <><i className="fas fa-trash-alt me-1"></i>Clear All</>
                  )}
                </button>
              </div>
            </div>
            <div className="card-body">
              <form onSubmit={handleAddBan} className="border rounded p-3 bg-light mb-4">
                <h6 className="mb-3">
                  <i className="fas fa-ban me-2"></i>
                  Add Banned Client
                </h6>
                <div className="row g-2 align-items-end">
                  <div className="col-md-3">
                    <label className="form-label small fw-medium">Ban By</label>
                    <select
                      className="form-select"
                      value={banType}
                      onChange={(e) => setBanType(e.target.value)}
                      disabled={!!banOperation}
                    >
                      <option value="clientid">Client ID</option>
                      <option value="username">Username</option>
                      <option value="peerhost">Source IP</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small fw-medium">
                      {banType === 'clientid' ? 'Client ID' : banType === 'username' ? 'Username' : 'Source IP'}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={banValue}
                      onChange={(e) => setBanValue(e.target.value)}
                      placeholder={banType === 'clientid' ? 'e.g. vv00003' : banType === 'username' ? 'e.g. device-user' : 'e.g. 192.168.1.20'}
                      disabled={!!banOperation}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label small fw-medium">Reason <span className="text-muted">(optional)</span></label>
                    <input
                      type="text"
                      className="form-control"
                      value={banReason}
                      onChange={(e) => setBanReason(e.target.value)}
                      placeholder="Why is this client banned?"
                      disabled={!!banOperation}
                    />
                  </div>
                  <div className="col-md-2 d-grid">
                    <button className="btn btn-danger" type="submit" disabled={!!banOperation}>
                      {banOperation === 'add' ? (
                        <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Adding…</>
                      ) : (
                        <><i className="fas fa-plus me-1"></i>Add Ban</>
                      )}
                    </button>
                  </div>
                </div>
              </form>

              {banActionMessage && (
                <div className="alert alert-success py-2 d-flex align-items-center" role="status">
                  <i className="fas fa-check-circle me-2"></i>
                  {banActionMessage}
                </div>
              )}
              {banActionError && (
                <div className="alert alert-danger py-2 d-flex align-items-center" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {banActionError}
                </div>
              )}
              {bansError && (
                <div className="alert alert-danger py-2 d-flex align-items-center mb-3" role="alert">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  <div>
                    <strong>Could not load banned clients.</strong> {bansError}
                    <div className="small mt-1">Confirm the portal API exposes the EMQX banned-client route, then try again.</div>
                  </div>
                </div>
              )}

              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div className="text-muted small">
                  {bansLoading ? 'Loading banned clients…' : `${bannedClients.length} banned client${bannedClients.length === 1 ? '' : 's'}`}
                </div>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleRemoveSelected}
                  disabled={selectedBans.length === 0 || bansLoading || !!banOperation}
                >
                  {banOperation === 'remove-selected' ? (
                    <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Removing…</>
                  ) : (
                    <><i className="fas fa-trash me-1"></i>Remove Selected ({selectedBans.length})</>
                  )}
                </button>
              </div>

              {bansLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading banned clients…</span>
                  </div>
                </div>
              ) : bannedClients.length === 0 && !bansError ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-user-check fa-3x mb-3 d-block opacity-25"></i>
                  <p className="mb-0">No banned clients found.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-sm align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '40px' }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            aria-label="Select all banned clients"
                            checked={bannedClients.length > 0 && selectedBans.length === bannedClients.length}
                            onChange={(e) => setSelectedBans(e.target.checked ? bannedClients.map(ban => ban.key) : [])}
                            disabled={!!banOperation}
                          />
                        </th>
                        <th>Ban Type</th>
                        <th>Client</th>
                        <th>Ban Time</th>
                        <th>Reason</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bannedClients.map((ban) => (
                        <tr key={ban.key}>
                          <td>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              aria-label={`Select ${ban.who}`}
                              checked={selectedBans.includes(ban.key)}
                              onChange={() => toggleBanSelection(ban.key)}
                              disabled={!!banOperation}
                            />
                          </td>
                          <td>
                            <span className="badge bg-secondary">
                              {ban.as === 'clientid' ? 'Client ID' : ban.as === 'username' ? 'Username' : ban.as === 'peerhost' ? 'Source IP' : ban.as}
                            </span>
                          </td>
                          <td><code>{ban.who}</code></td>
                          <td className="small text-muted">{formatBanDate(ban.at || ban.created_at)}</td>
                          <td className="small">{ban.reason || '-'}</td>
                          <td className="text-end">
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleRemoveBan(ban)}
                              disabled={!!banOperation}
                            >
                              {banOperation === `remove:${ban.key}` ? (
                                <span className="spinner-border spinner-border-sm" role="status"></span>
                              ) : (
                                <><i className="fas fa-trash me-1"></i>Remove</>
                              )}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MqttCommands
